import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  Table,
  Input,
  Button,
  FormGroup,
} from "reactstrap";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import NF from "../../../assets/images/file.png";
import ConfirmModal from "../../../Components/Common/ConfirmModal";

import CustomPagination from "../../../Components/Common/CustomePagination";
import {
  addSession,
  deleteSession,
  editSession,
  fetchSessionsList,
  updateSessionStatus,
} from "../../../slices/Session/thunk";
import AddSessionModal from "../../../Modals/Session/AddSessionModal";
import EditSessionModal from "../../../Modals/Session/EditSessionModal";
// import AddSessionModal from "../../../Modals/Session/AddSessionModal";
// import EditSessionModal from "../../../Modals/Session/EditSessionModal";
// import ViewSessionModal from "../../../Modals/Session/ViewSessionModal";

const Session = () => {
  const dispatch = useDispatch();

  // Pagination & Search
  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debounceSearch, setDebounceSearch] = useState("");

  // Modals and Selection
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionIdToDelete, setSessionIdToDelete] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Selectors
  const selectSessionState = (state) => state;
  const sessionSelector = createSelector(selectSessionState, (state) => ({
    sessions: state.Session.sessions,
    loading: state.Session.loading,
    error: state.Session.error,
    pagination: state.Session.pagination,
  }));

  const { sessions, loading, error, pagination } = useSelector(sessionSelector);

  // Modal Toggles
  const toggleAddModal = () => setIsAddModalOpen(!isAddModalOpen);
  const toggleEditModal = () => setIsEditModalOpen(!isEditModalOpen);
  const toggleConfirmModal = () => setIsConfirmModalOpen(!isConfirmModalOpen);

  // Debounce search
  useEffect(() => {
    const timeout = setTimeout(() => setDebounceSearch(searchTerm), 500);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  // Fetch sessions
  useEffect(() => {
    dispatch(
      fetchSessionsList({
        pageNo,
        pageSize,
        sortBy: "createdAt",
        sortOrder: "DESC",
        isPagination: 1,
        searchTerm: debounceSearch.trim(),
      })
    );
  }, [dispatch, pageNo, pageSize, debounceSearch]);

  // CRUD Handlers
  const handleAddSession = async (data) => {
    await dispatch(addSession(data));
    toggleAddModal();
    dispatch(
      fetchSessionsList({
        pageNo,
        pageSize,
        sortBy: "createdAt",
        sortOrder: "DESC",
        isPagination: 1,
      })
    );
  };

  const handleToggleStatus = async (sessionId, currentStatus) => {
    const newStatus = {
      sessionAction: !currentStatus,
    };
    await dispatch(updateSessionStatus(sessionId, newStatus));
    const params = {
      pageNo,
      pageSize,
      sortBy: "createdAt",
      sortOrder: "DESC",
      isPagination: 1,
    };
    await dispatch(
      fetchSessionsList({
        pageNo,
        pageSize,
        sortBy: "createdAt",
        sortOrder: "DESC",
        isPagination: 1,
      })
    );
  };

  const handleEditClick = (session) => {
    setSelectedSession(session);
    setModalOpen(true);
  };

  const handleUpdateSession = async (data) => {
    await dispatch(editSession(data));
    toggleEditModal();
    dispatch(
      fetchSessionsList({
        pageNo,
        pageSize,
        sortBy: "createdAt",
        sortOrder: "DESC",
        isPagination: 1,
      })
    );
  };

  const handleViewClick = (session) => {
    setSelectedSession(session);
    setIsViewModalOpen(true);
  };

  const handleDeleteClick = (id) => {
    setSessionIdToDelete(id);
    setIsConfirmModalOpen(true);
  };

  const confirmDelete = async () => {
    if (sessionIdToDelete) {
      await dispatch(deleteSession(sessionIdToDelete));
      setSessionIdToDelete(null);
      toggleConfirmModal();
      dispatch(
        fetchSessionsList({
          pageNo,
          pageSize,
          sortBy: "createdAt",
          sortOrder: "DESC",
          isPagination: 1,
        })
      );
    }
  };

  document.title = "Session List";

  return (
    <div className="page-content">
      <Container fluid>
        <Card>
          <CardHeader className="d-flex align-items-center flex-wrap gap-2">
            <h4 className="card-title mb-0 me-2">Session List</h4>

            <div className="search-box ms-3">
              <Input
                type="text"
                className="form-control search"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPageNo(0);
                }}
              />
              <i className="ri-search-line search-icon"></i>
            </div>

            <div className="ms-auto">
              <Button
                color="primary"
                size="sm"
                onClick={toggleAddModal}
                className="btn btn-primary btn-sm"
              >
                <i className="ri-add-line align-bottom me-1"></i>
                Add Session
              </Button>
            </div>
          </CardHeader>

          <CardBody>
            <div className="table-responsive">
              <Table className="table align-middle table-nowrap text-start">
                <thead className="table-light">
                  <tr>
                    <th>Sr No</th>
                    <th>Session Name</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Status</th>

                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    [...Array(pageSize)].map((_, i) => (
                      <tr key={i}>
                        {[...Array(6)].map((__, j) => (
                          <td key={j}>
                            <Skeleton height={25} />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : error ? (
                    <tr>
                      <td colSpan="6" className="text-danger text-center">
                        {error}
                      </td>
                    </tr>
                  ) : sessions?.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center text-danger">
                        <img
                          src={NF}
                          alt="No File"
                          style={{ width: "500px", marginTop: "10px" }}
                        />
                      </td>
                    </tr>
                  ) : (
                    sessions?.map((session, index) => (
                      <tr key={session.id}>
                        <td>{pageNo * pageSize + index + 1}</td>
                        <td>{session.name}</td>
                        <td>{session.startDate}</td>
                        <td>{session.endDate}</td>
                        {/* <td>
                          {session.active ? (
                            <span className="badge bg-success">Active</span>
                          ) : (
                            <span className="badge bg-danger">Inactive</span>
                          )}
                        </td> */}

                        <td onClick={(e) => e.stopPropagation()}>
                          <FormGroup switch>
                            <Input
                              type="switch"
                              id={`userActiveSwitch-${session?.id}`}
                              checked={session?.active}
                              onChange={() =>
                                handleToggleStatus(session?.id, session?.active)
                              }
                              className={
                                session?.active
                                  ? "form-check-input bg-primary border-success"
                                  : ""
                              }
                            />
                          </FormGroup>
                        </td>

                        <td>
                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-success btn-icon"
                              onClick={() => handleEditClick(session)}
                            >
                              <i className="ri-edit-2-line"></i>
                            </button>

                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger btn-icon"
                              onClick={() => handleDeleteClick(session.id)}
                            >
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>

            <CustomPagination
              currentPage={pagination?.currentPage}
              totalPages={pagination?.totalPages}
              onPageChange={(page) => setPageNo(page)}
              pageSize={pageSize}
              setPageSize={setPageSize}
              setPageNo={setPageNo}
            />
          </CardBody>
        </Card>
        {/* Modals */}
        <AddSessionModal
          isOpen={isAddModalOpen}
          toggle={toggleAddModal}
          onSubmit={handleAddSession}
        />
        {selectedSession && (
          <EditSessionModal
            isOpen={modalOpen}
            toggle={() => setModalOpen(!modalOpen)}
            onSubmit={handleUpdateSession}
            sessionData={selectedSession}
          />
        )}

        {/* <ViewSessionModal
          isOpen={isViewModalOpen}
          toggle={toggleViewModal}
          session={selectedSession}
        />
        */}
        <ConfirmModal
          isOpen={isConfirmModalOpen}
          toggle={toggleConfirmModal}
          title="Delete Session"
          message="Are you sure you want to delete this session?"
          onConfirm={confirmDelete}
        />
      </Container>
    </div>
  );
};

export default Session;
